/**
 * The URL to frame, with a cache key the stale copy cannot match.
 *
 * The static host serves every document `cache-control: public, max-age=3600`,
 * including the placeholder it answers with during the routing gap. So a
 * browser that touched the address before the deploy landed — the user clicking
 * the link above, a frame from an earlier build — holds that placeholder for an
 * hour and will keep rendering it no matter what the site actually serves now.
 *
 * The server-side probe cannot see any of this: it sends `Cache-Control:
 * no-cache` and gets the truth, while the frame gets the browser's copy. Only
 * the frame can fix it, by asking for a URL that is not in the cache. The param
 * changes nothing about what the host serves — the path is still `/`, and the
 * SPA ignores an unknown query — it only changes the cache key.
 *
 * A reload advances `loadKey` for the same reason. Pointing the frame at the URL
 * it is already on would be served from cache and reload nothing, which is the
 * failure the user reports as "the refresh button does nothing".
 */
export function frameUrl(siteUrl: string, loadKey: number): string {
  try {
    const url = new URL(siteUrl);
    url.searchParams.set("hatch_t", String(loadKey));
    return url.href;
  } catch {
    // Not parseable is not a reason to lose the preview; frame it as given.
    return siteUrl;
  }
}

/**
 * Whether this address is safe to hand to an `href` or an iframe `src`.
 *
 * The site URL is not ours. It is scraped from the deploy step's stdout inside
 * the agent's own workspace, which means its content is downstream of a model
 * and of whatever the build printed. The API already declines to trust it —
 * `site-probe.ts` constrains what it will fetch — and deliberately forwards
 * non-conforming values to the browser rather than dropping them.
 *
 * So the check has to happen again here, because `new URL()` accepts plenty
 * that must never reach a link: `javascript:` and `data:` both parse, and the
 * iframe's sandbox does not apply to an anchor sitting in Hatch's own origin.
 * The blast radius is only ever the session's own viewer — every session route
 * is bound to a signed cookie carrying that session id, so no one can be handed
 * someone else's URL — but self-inflicted is still inflicted.
 *
 * An allowlist rather than a denylist: two schemes can serve a website, and
 * anything else is a value we should be showing as text, not following.
 */
export function isRenderableSiteUrl(siteUrl: string): boolean {
  try {
    const { protocol } = new URL(siteUrl);
    // `http:` is here for local development against a non-TLS host; deploys are
    // always https.
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/**
 * The `sandbox` the preview frame carries.
 *
 * The base grant is what a generated site needs to be itself: scripts, forms,
 * and a link that opens a tab. `allow-same-origin` is added only when the site
 * lives somewhere other than Hatch, and both halves of that sentence are
 * load-bearing.
 *
 * WHY IT HAS TO BE ADDED. Without it the frame is assigned an OPAQUE origin,
 * and in an opaque origin every storage accessor THROWS rather than returning
 * empty — `localStorage` included. A generated app that reads a saved name or
 * theme while it is starting up therefore dies before it mounts, and what the
 * visitor sees is a white rectangle with nothing in the console but a
 * SecurityError from a bundle they did not write. The site itself is perfectly
 * healthy: open the same URL in a tab and it works. This was reported as "the
 * preview is broken" for exactly that reason — the one place it fails is the
 * one place we show it.
 *
 * WHY IT CANNOT BE ADDED UNCONDITIONALLY. `allow-scripts` together with
 * `allow-same-origin` on a document from OUR OWN origin is the well-known
 * sandbox escape: the framed page is then same-origin with Hatch, can reach
 * through `parent`, and can even rewrite this very attribute off itself. It
 * would hold Hatch's session cookie in its hands. A deployed site is always on
 * a Xano host and never on Hatch's, so in practice this never happens — but
 * `siteUrl` is scraped from the deploy's stdout inside the agent's workspace,
 * which makes it downstream of a model, and "in practice" is not the standard
 * to apply to a value with that provenance. See `isRenderableSiteUrl` above,
 * which exists for the same reason and is not sufficient on its own: it checks
 * the SCHEME, and a `https://` URL pointing at Hatch passes it.
 *
 * So the grant is conditional on a real origin comparison, and anything this
 * function cannot parse or cannot prove is cross-origin keeps the strict
 * sandbox. A site that stores nothing renders identically either way; a site
 * that stores something renders instead of dying. Neither can touch Hatch.
 *
 * A caveat worth stating because it limits what this fixes: a cross-site frame
 * gets PARTITIONED storage in Chrome (which works, and is what the booth
 * monitor runs) but BLOCKED storage in Safari's default configuration. So a
 * visitor opening their build on an iPhone can still hit the original throw.
 * That half is not fixable from here — it is why the prompt also asks generated
 * code to guard its storage access.
 */
export function frameSandbox(siteUrl: string): string {
  const base = "allow-scripts allow-forms allow-popups";
  return isCrossOrigin(siteUrl) ? `${base} allow-same-origin` : base;
}

/**
 * Whether `siteUrl` is definitely somewhere other than the page we are running
 * in.
 *
 * Fails CLOSED: an address that will not parse, or one this code cannot compare
 * because there is no `window` to compare it against, answers `false` and gets
 * the strict sandbox. The question being asked is "can I prove this is not us",
 * not "is this probably fine".
 */
function isCrossOrigin(siteUrl: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URL(siteUrl, window.location.href).origin !== window.location.origin;
  } catch {
    return false;
  }
}
